import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { formatNZD, timeAgo } from "@/lib/format";
import { Loader2, CalendarClock, ExternalLink, MapPin } from "lucide-react";

/**
 * My viewing requests — buyer's submitted property viewings.
 * Backend: GET /classifieds/my/viewing-requests
 */
const FALLBACK = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=400&q=60";

const STATUS_TONE = {
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  accepted: { label: "Accepted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  declined: { label: "Declined", cls: "bg-red-50 text-red-700 border-red-200" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  completed: { label: "Completed", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function MyViewingRequestsPage() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["my-viewing-requests", user?.id],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/my/viewing-requests");
      return data?.requests || data?.viewing_requests || data || [];
    },
    enabled: !!user,
    retry: 0,
  });

  const items = Array.isArray(q.data) ? q.data : [];

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="My viewing requests" description="Property viewings you have requested." />
      <Breadcrumbs items={[{ label: "My viewing requests" }]} />

      <div className="mt-6">
        <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900">My viewing requests</h1>
        <p className="mt-2 text-slate-500">Property viewings you have booked with sellers and agents.</p>
      </div>

      {q.isLoading && (
        <div className="mt-10 flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!q.isLoading && q.isError && (
        <div
          data-testid="my-viewings-error"
          className="mt-10 rounded-2xl bg-white shadow-card p-10 text-center"
        >
          <div className="w-12 h-12 mx-auto rounded-full bg-red-50 text-red-600 grid place-items-center">
            <CalendarClock className="w-5 h-5" />
          </div>
          <h2 className="mt-4 font-display text-lg font-bold text-slate-900">Couldn&apos;t load your requests</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">The service is briefly unavailable. Please try again.</p>
          <button
            type="button"
            onClick={() => q.refetch()}
            data-testid="my-viewings-retry"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-sm font-semibold px-5 py-2 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!q.isLoading && !q.isError && items.length === 0 && (
        <div className="mt-10 rounded-2xl bg-white shadow-card p-12 text-center" data-testid="my-viewings-empty">
          <div className="w-14 h-14 mx-auto rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center">
            <CalendarClock className="w-6 h-6" />
          </div>
          <h2 className="mt-4 font-display text-xl font-bold text-slate-900">No viewing requests yet</h2>
          <p className="mt-2 text-slate-500 max-w-md mx-auto">
            Book a property viewing from any listing and it will show up here.
          </p>
          <Link
            to="/c/property"
            data-testid="my-viewings-browse"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-semibold text-sm px-5 py-2.5 transition-colors"
          >
            Browse properties
          </Link>
        </div>
      )}

      {!q.isLoading && !q.isError && items.length > 0 && (
        <div className="mt-8 space-y-3" data-testid="my-viewings-list">
          {items.map((r) => {
            const status = String(r.status || "pending").toLowerCase();
            const tone = STATUS_TONE[status] || STATUS_TONE.pending;
            const when = r.when || r.requested_at || r.scheduled_at || r.slot;
            const whenLabel = when ? new Date(when).toLocaleString("en-NZ", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : null;
            return (
              <div
                key={r.id}
                data-testid={`my-viewing-${r.id}`}
                className="rounded-2xl bg-white shadow-card p-4 md:p-5 flex flex-col md:flex-row gap-4"
              >
                <Link to={`/ad/${r.listing_id}`} className="shrink-0">
                  <img
                    src={r.listing_image || r.listing?.image || FALLBACK}
                    alt=""
                    className="w-full md:w-32 h-24 md:h-24 rounded-xl object-cover"
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <Link
                      to={`/ad/${r.listing_id}`}
                      className="font-display font-bold text-slate-900 hover:text-[color:var(--brand-blue)] transition-colors truncate"
                      data-testid={`my-viewing-title-${r.id}`}
                    >
                      {r.listing_title || r.listing?.title || "Listing"}
                    </Link>
                    <span
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${tone.cls}`}
                      data-testid={`my-viewing-status-${r.id}`}
                    >
                      {tone.label}
                    </span>
                  </div>
                  {(r.city || r.region || r.address) && (
                    <div className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
                      <MapPin className="w-3 h-3" /> {r.address || [r.city, r.region].filter(Boolean).join(", ")}
                    </div>
                  )}
                  {whenLabel && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-800">
                      <CalendarClock className="w-3.5 h-3.5 text-slate-500" />
                      {whenLabel}
                    </div>
                  )}
                  {r.message && (
                    <p className="mt-2 text-sm text-slate-600 line-clamp-2 whitespace-pre-wrap">{r.message}</p>
                  )}
                  {r.seller_name && (
                    <div className="mt-2 text-xs text-slate-500">
                      With <span className="font-semibold text-slate-700">{r.seller_name}</span>
                      {r.price != null && <> · asking {formatNZD(r.price)}</>}
                    </div>
                  )}
                  <div className="mt-2 text-[11px] text-slate-400">Requested {timeAgo(r.created_at)}</div>
                </div>
                <div className="shrink-0 flex items-start">
                  <Link
                    to={`/ad/${r.listing_id}`}
                    data-testid={`my-viewing-open-${r.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-xs px-3.5 py-2 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> View listing
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
